import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Message } from 'react-chat-ui';
import { FaMicrophone } from 'react-icons/fa';
import bg from '../Assets/bg.png'


const PdfChatApp = () => {
    const [chatMessages, setChatMessages] = useState([]);
    const [query, setQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [isChatOpen, setIsChatOpen] = useState(false);

    const chatEndRef = useRef(null); // Ref for the chat container to auto-scroll
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

    // Automatically call the /start endpoint when the component mounts (on page load)
    useEffect(() => {
        const startChat = async () => {
            try {
                const response = await axios.post(`${API_BASE_URL}/start`);
                setIsChatOpen(true); // Open chat after loading the text files
            } catch (error) {
                console.error('Error triggering /start endpoint:', error);
            }
        };

        startChat(); // Trigger /start on page load (refresh)
    }, []); // Empty dependency array ensures this runs only on component mount

    // Scroll to the bottom of the chat feed whenever a new message is added
    useEffect(() => {
        if (chatMessages.length > 0) {
            chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [chatMessages]); // This will run every time chatMessages is updated

    const handleQuerySubmit = async (spokenQuery = null) => {
        const finalQuery = spokenQuery || query.trim();
        if (!finalQuery) return;

        // Clear the input box as soon as the query is submitted
        setQuery('');

        setChatMessages(prev => [
            ...prev,
            new Message({ id: 0, message: finalQuery, senderName: 'User' }),
        ]);
        setLoading(true);

        try {
            const response = await axios.post(`${API_BASE_URL}/query`, {
                query: finalQuery,
            });
            const answer = response.data.answer;

            setChatMessages(prev => [
                ...prev,
                new Message({ id: 1, message: answer, senderName: 'AI' }),
            ]);
        } catch (error) {
            console.error('Error getting LLM response:', error);
        } finally {
            setLoading(false);
            // setQuery(''); ← Already cleared earlier
        }
    };



    const handleVoiceInput = () => {
        const SpeechRecognition =
            window.SpeechRecognition || window.webkitSpeechRecognition;

        if (!SpeechRecognition) {
            alert('Your browser does not support speech recognition.');
            return;
        }

        const recognition = new SpeechRecognition();
        recognition.lang = 'en-US';
        recognition.interimResults = false;
        recognition.maxAlternatives = 1;

        recognition.start();

        recognition.onstart = () => {
            console.log('Voice recognition started...');
        };

        recognition.onresult = (event) => {
            const transcript = event.results[0][0].transcript;
            console.log('You said:', transcript);
            setQuery(transcript);       // Fill in the input field
            setTimeout(() => {
                handleQuerySubmit(transcript); // Auto-send
            }, 100); // Slight delay to ensure state updates
        };

        recognition.onerror = (event) => {
            console.error('Speech recognition error', event.error);
        };
    };

    // Handle the Enter key to submit the query
    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleQuerySubmit();
        }
    };

    return (
        <div className="chat-container">
            {isChatOpen && (
                <div className="chat-box">
                    <div className="chat-feed" style={{ maxHeight: '80vh', overflowY: 'auto', paddingRight: '10px' }}>
                        {chatMessages.map((msg, index) => (
                            <div
                                key={index}
                                style={{
                                    margin: '8px 0',
                                    textAlign: msg.id === 0 ? 'right' : 'left',
                                    color: msg.id === 1 ? 'black' : '#070808',
                                    fontSize: '16px',
                                    whiteSpace: 'pre-wrap',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'inline-block',
                                        background: msg.id === 0 ? '#dcf8c6' : '#f1f1f1',
                                        padding: '10px 14px',
                                        borderRadius: '12px',
                                        maxWidth: '70%',
                                        animation: 'fadeIn 0.3s ease-in-out',
                                    }}
                                >
                                    {msg.message}
                                </div>
                            </div>
                        ))}

                        {/* AI Typing Indicator */}
                        {loading && (
                            <div
                                style={{
                                    textAlign: 'left',
                                    paddingLeft: '10px',
                                    paddingBottom: '4px',
                                    color: 'black',
                                    fontSize: '16px',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'inline-block',
                                        background: '#f1f1f1',
                                        padding: '10px 14px',
                                        borderRadius: '12px',
                                        maxWidth: '70%',
                                    }}
                                >
                                    <span className="dot-flash">Typing<span className="dot">.</span><span className="dot">.</span><span className="dot">.</span></span>
                                </div>
                            </div>
                        )}
                        <div ref={chatEndRef} />
                    </div>


                    <div className="chat-input" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {/* Row with Input + Mic */}
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <input
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Ask something..."
                                className="chat-input-field"
                                onKeyDown={handleKeyPress}
                                style={{
                                    flex: 1,
                                    padding: '10px',
                                    borderRadius: '6px',
                                    border: '1px solid #ccc',
                                    fontSize: '16px',
                                }}
                            />
                            <button
                                onClick={handleVoiceInput}
                                disabled={loading}
                                className="chat-mic-btn"
                                style={{
                                    marginLeft: '8px',
                                    padding: '10px',
                                    marginTop: '-10px',
                                    borderRadius: '6px',
                                    border: 'none',
                                    background: '#f1f1f1',
                                    cursor: 'pointer',
                                }}
                                title="Speak your query"
                            >
                                <FaMicrophone size={18} />
                            </button>
                        </div>

                        {/* Ask button below */}
                        <button
                            onClick={() => handleQuerySubmit()}
                            disabled={loading}
                            className={`chat-submit-btn ${loading ? 'loading' : ''}`}
                            style={{
                                padding: '10px',
                                borderRadius: '6px',
                                backgroundColor: '#007bff',
                                color: '#fff',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '16px',
                                fontWeight: 'bold',
                                width: '100%',
                            }}
                        >
                            {loading ? 'Thinking...' : 'Ask'}
                        </button>
                    </div>


                </div>
            )}
            {!isChatOpen && (
                <div className="loading-wrapper">
                    <div className="loading-background" />
                    <div className="loading-content">
                        <div className="loader" />
                        <div className="loading-text">Initializing chat...</div>
                    </div>
                </div>
            )}


            <div ref={chatEndRef} /> {/* Empty div to scroll to the bottom */}
        </div>
    );
};

export default PdfChatApp;
